// Compact race results / payoffs panel — shown inside the Runners card after
// a race has posted results. Consistent for single pool and combos.

const POOL_ORDER = ["exacta", "tri", "spr", "dbl", "p3", "p4", "p5", "p6", "quinella", "omni", "pen"];
const POOL_LABELS = {
  exacta: "Exacta",
  tri: "Trifecta",
  spr: "Superfecta",
  dbl: "Daily Double",
  p3: "Pick 3",
  p4: "Pick 4",
  p5: "Pick 5",
  p6: "Pick 6",
  quinella: "Quinella",
  omni: "Omni",
  pen: "Pentafecta",
};

// Canonicalize a pool code (raw feed codes OR already-normalized names) to the
// lowercase name the UI groups/labels by. Makes the panel resilient to a backend
// that returns either "EX" or "exacta" (a casing mismatch used to break the layout).
const POOL_CANON = {
  WIN: "win", PLC: "place", PLACE: "place", SHW: "show", SHOW: "show",
  EX: "exacta", EXACTA: "exacta", TRI: "tri", TRIFECTA: "tri",
  SPR: "spr", SUP: "spr", SUPERFECTA: "spr", DBL: "dbl", "DAILY DOUBLE": "dbl",
  PK3: "p3", P3: "p3", PK4: "p4", P4: "p4", PK5: "p5", P5: "p5",
  PK6: "p6", P6: "p6", QU: "quinella", QUINELLA: "quinella",
  OMN: "omni", OMNI: "omni", PEN: "pen",
};
const normPool = (c) => POOL_CANON[(c || "").toUpperCase()] || (c || "").toLowerCase();

const pnClass = (n) => {
  const num = parseInt(n, 10);
  return `pn-badge ${num >= 1 && num <= 24 ? `pn${num}` : "pn-default"}`;
};

const clean = (s) => (s || "").toString().trim();

export const RacePayoffs = ({ payouts, entries }) => {
  if (!payouts || payouts.length === 0) return null;

  // Win/Place/Show grouped by program number
  const byNum = {};
  payouts.forEach((p) => {
    const pool = normPool(p.pool);
    if (["win", "place", "show"].includes(pool)) {
      const n = clean(p.winners);
      byNum[n] = byNum[n] || { num: n };
      byNum[n][pool] = p.payout;
    }
  });
  const nameFor = (n) => {
    const e = entries?.find((x) => String(x.programNumber) === n);
    return e?.horseName || `Horse ${n}`;
  };
  const finishers = Object.values(byNum).sort((a, b) => {
    const rank = (x) => (x.win != null ? 0 : x.place != null ? 1 : 2);
    return rank(a) - rank(b);
  });

  // Exotic / multi-race pool payoffs
  const poolPayoffs = payouts
    .filter((p) => !["win", "place", "show"].includes(normPool(p.pool)))
    .sort((a, b) => {
      const ia = POOL_ORDER.indexOf(normPool(a.pool));
      const ib = POOL_ORDER.indexOf(normPool(b.pool));
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

  const fmt = (v) => (v != null ? `$${(Number(v) * 2).toFixed(2)}` : "-");

  return (
    <div className="mt-4 border-t border-slate-700 pt-3" data-testid="race-payoffs">
      <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-400 mb-2">
        Race Payoffs
      </div>

      {finishers.length > 0 && (
        <div className="mb-3">
          {/* header */}
          <div className="grid grid-cols-[auto_1fr_44px_44px_44px] gap-1 text-[10px] uppercase text-slate-500 px-1 pb-1">
            <span>#</span>
            <span>Horse</span>
            <span className="text-right">Win</span>
            <span className="text-right">Plc</span>
            <span className="text-right">Shw</span>
          </div>
          {finishers.map((f) => (
            <div
              key={f.num}
              className="grid grid-cols-[auto_1fr_44px_44px_44px] gap-1 items-center text-xs px-1 py-1 border-b border-slate-800"
              data-testid={`payoff-runner-${f.num}`}
            >
              <span className={pnClass(f.num)}>{f.num}</span>
              <span className="text-slate-300 truncate">{nameFor(f.num)}</span>
              <span className={`text-right ${f.win != null ? "text-green-400 font-semibold" : "text-slate-600"}`}>
                {fmt(f.win)}
              </span>
              <span className={`text-right ${f.place != null ? "text-green-400 font-semibold" : "text-slate-600"}`}>
                {fmt(f.place)}
              </span>
              <span className={`text-right ${f.show != null ? "text-green-400 font-semibold" : "text-slate-600"}`}>
                {fmt(f.show)}
              </span>
            </div>
          ))}
        </div>
      )}

      {poolPayoffs.length > 0 && (
        <div>
          <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-400 mb-1 px-1">
            Race Payoff ($1)
          </div>
          {poolPayoffs.map((p, i) => (
            <div
              key={i}
              className="grid grid-cols-[80px_1fr_auto] gap-1 items-center text-xs px-1 py-1 border-b border-slate-800"
              data-testid={`payoff-pool-${normPool(p.pool)}`}
            >
              <span className="text-slate-400">{POOL_LABELS[normPool(p.pool)] || p.pool.toUpperCase()}</span>
              <span className="font-mono text-slate-300 truncate">{clean(p.winners)}</span>
              <span className="text-right text-green-400 font-semibold">
                ${Number(p.payout).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RacePayoffs;
